import {
  PLATFORM_VERTICAL_KEYS,
  REGISTRY_BLOCK,
  THEME_ID,
  type PlatformVerticalKey,
  type RegistryBlockType,
  type ThemeId,
} from '../catalog/platformCatalogIds';
import {
  DEFAULT_SITE_LOCALE,
  PLATFORM_LOCALE_ONBOARDING_DEFAULTS,
  ProjectLocalePolicySchema,
  type ProjectLocalePolicy,
} from '../locales/platformLocales';
import { PAGE_SLUG } from '../routing/pageRouting';

const T = REGISTRY_BLOCK.type;

/** Service-recipe families — own pages / allow-deny / CTA; theme stays visual. */
export const FAMILY_ID = {
  fineDining: 'fine_dining',
  bistro: 'bistro',
  /**
   * Asian neighborhood — Vietnam / pan-Asian / sushi counter as themes.
   * Menu soul + locale diverge by theme; one dining IA (Speisekarte · About · reserve).
   */
  asian: 'asian',
  cafe: 'cafe',
  bar: 'bar',
  foodTruck: 'food_truck',
  imbiss: 'imbiss',
} as const;

export const FAMILY_ID_VALUES = [
  FAMILY_ID.fineDining,
  FAMILY_ID.bistro,
  FAMILY_ID.asian,
  FAMILY_ID.cafe,
  FAMILY_ID.bar,
  FAMILY_ID.foodTruck,
  FAMILY_ID.imbiss,
] as const;

export type FamilyId = (typeof FAMILY_ID_VALUES)[number];

/**
 * Former service-family ids folded into Asian neighborhood.
 * Kept so stored `familyId` / handoffs still resolve.
 */
export const LEGACY_FAMILY_ID_ALIASES = {
  sushi: FAMILY_ID.asian,
  vietnam: FAMILY_ID.asian,
} as const satisfies Record<string, FamilyId>;

/** Sushi-counter theme under Asian neighborhood (omakase / Theke soul). */
export function isHinokiCounterTheme(themeId?: string | null): boolean {
  return themeId === THEME_ID.hinokiCounter;
}

/** Vietnamese neighborhood themes (pho / bun soul + de+vi seed). */
export function isVietnamNeighborhoodTheme(themeId?: string | null): boolean {
  return themeId === THEME_ID.lotusSteam || themeId === THEME_ID.tileAwning;
}

/** Pan-Asian court theme (ramen / bowls / dim sum soul). */
export function isLanternCourtTheme(themeId?: string | null): boolean {
  return themeId === THEME_ID.lanternCourt;
}

/** Currywurst / German street soul under Imbiss. */
export function isMustardRailTheme(themeId?: string | null): boolean {
  return themeId === THEME_ID.mustardRail;
}

/** Falafel / mezze window soul under Imbiss. */
export function isTahiniWindowTheme(themeId?: string | null): boolean {
  return themeId === THEME_ID.tahiniWindow;
}

export const FAMILY_PRIMARY_CTA = {
  reserve: 'reserve',
  call: 'call',
  route: 'route',
  order: 'order',
} as const;

export type FamilyPrimaryCta = (typeof FAMILY_PRIMARY_CTA)[keyof typeof FAMILY_PRIMARY_CTA];

/**
 * Family = service recipe (Option A).
 * `pages` when set intersects the vertical/siteMode slug set.
 * `excludeBlocks` always wins over tier entitlement and theme `homeIncludeOptional`.
 */
export interface FamilyRecipe {
  id: FamilyId;
  /** Verticals this recipe applies to. Wrong vertical → fall back to vertical default. */
  verticals: readonly PlatformVerticalKey[];
  /**
   * Allowlist of bootstrap page slugs. Omit to keep vertical/siteMode defaults.
   * Include impressum + datenschutz when set.
   */
  pages?: readonly string[];
  /** Hard deny even if tier allows or theme composition re-includes. */
  excludeBlocks: readonly RegistryBlockType[];
  primaryCta: FamilyPrimaryCta;
  /** Seed voice hint for bootstrap copy. */
  copyTone?: 'editorial' | 'street' | 'counter' | 'cafe' | 'bar' | 'route';
  /** Arrangement hints — theme presentations still win when set. */
  menuHighlight?: string;
  menuFull?: string;
  /**
   * Suggested customer-site locales for onboarding / project create.
   * Omit → platform defaults (`de` + `en`).
   */
  seedLocalePolicy?: {
    defaultLocale: string;
    supportedLocales: readonly string[];
  };
}

/** When theme.familyId is missing or not valid for the vertical. */
export const DEFAULT_FAMILY_BY_VERTICAL: Record<PlatformVerticalKey, FamilyId> = {
  [PLATFORM_VERTICAL_KEYS.restaurant]: FAMILY_ID.fineDining,
  [PLATFORM_VERTICAL_KEYS.cafe]: FAMILY_ID.cafe,
  [PLATFORM_VERTICAL_KEYS.bar]: FAMILY_ID.bar,
  [PLATFORM_VERTICAL_KEYS.foodTruck]: FAMILY_ID.foodTruck,
};

const LEGAL_AND_HOME = [PAGE_SLUG.index, PAGE_SLUG.impressum, PAGE_SLUG.datenschutz] as const;

const DINING_PAGES = [
  ...LEGAL_AND_HOME,
  PAGE_SLUG.menu,
  PAGE_SLUG.aboutUs,
  PAGE_SLUG.contact,
] as const;

/**
 * Market-backed recipes (DE 2026). Imbiss fully applied in bootstrap;
 * others declare exclude/CTA/menu so theme composition + tier stay aligned.
 */
export const FAMILY_RECIPE: Record<FamilyId, FamilyRecipe> = {
  /**
   * Michelin / gourmet — Haerlin, Orangerie patterns.
   * Menü + Chef/Team + Online-Reservierung; tasting belongs on Home when entitled.
   */
  [FAMILY_ID.fineDining]: {
    id: FAMILY_ID.fineDining,
    verticals: [PLATFORM_VERTICAL_KEYS.restaurant],
    pages: [...DINING_PAGES],
    excludeBlocks: [
      T.cateringInquiry,
      T.liveGps,
      T.popUpTicket,
      T.preOrderPickup,
      T.coffeeSubscription,
      /**
       * Theme Speisekarte (Editorial Board / Grid / Dish Cards) + Weinkarte —
       * not live Dynamic Menu / Category Chips, which clash with Cellar ledger.
       */
      T.dynamicMenu,
    ],
    primaryCta: FAMILY_PRIMARY_CTA.reserve,
    copyTone: 'editorial',
    menuHighlight: 'chefsCut',
    menuFull: 'editorial',
  },
  /**
   * Neighbourhood bistro — menu preview + reserve / walk-in hours.
   * No tasting-menu default (fine-dining spine).
   * Gallery Speisekarte on /menu — not live Dynamic Menu Flow.
   */
  [FAMILY_ID.bistro]: {
    id: FAMILY_ID.bistro,
    verticals: [PLATFORM_VERTICAL_KEYS.restaurant],
    pages: [...DINING_PAGES],
    excludeBlocks: [
      T.tastingTimeline,
      T.cateringInquiry,
      T.liveGps,
      T.popUpTicket,
      T.preOrderPickup,
      T.coffeeSubscription,
      T.drinksList,
      T.privatePartyInquiry,
      /** Category Gallery Speisekarte — not live Dynamic Menu Flow. */
      T.dynamicMenu,
    ],
    primaryCta: FAMILY_PRIMARY_CTA.reserve,
    copyTone: 'editorial',
    menuHighlight: 'zine',
    menuFull: 'gallery',
  },
  /**
   * Asian neighborhood — Pho Co / Jaadin / ramen courts / sushi Theke as themes.
   * Dining IA shared; tasting only seeded on Hinoki (theme composition).
   * Vietnam themes seed de+vi via `resolveFamilySeedLocalePolicy(..., themeId)`.
   */
  [FAMILY_ID.asian]: {
    id: FAMILY_ID.asian,
    verticals: [PLATFORM_VERTICAL_KEYS.restaurant],
    pages: [...DINING_PAGES],
    excludeBlocks: [
      T.cateringInquiry,
      T.liveGps,
      T.popUpTicket,
      T.preOrderPickup,
      T.coffeeSubscription,
      T.cocktailSpotlight,
      T.drinksList,
      T.privatePartyInquiry,
      /** Theme Speisekarte (chips / gallery / editorial) — not live Dynamic Menu Flow. */
      T.dynamicMenu,
    ],
    primaryCta: FAMILY_PRIMARY_CTA.reserve,
    copyTone: 'editorial',
    menuHighlight: 'dishCards',
    menuFull: 'chips',
  },
  /**
   * Café — THE BARN / Einstein: hours · map · Speisen/Getränke.
   * Specialty coffee does not default to Tisch reservieren.
   * No dedicated `/menu` page — bakery counter + hours live on Home.
   */
  [FAMILY_ID.cafe]: {
    id: FAMILY_ID.cafe,
    verticals: [PLATFORM_VERTICAL_KEYS.cafe],
    pages: [...LEGAL_AND_HOME, PAGE_SLUG.aboutUs, PAGE_SLUG.contact],
    excludeBlocks: [
      T.reservation,
      T.chefStory,
      T.kitchenTeam,
      T.tastingTimeline,
      T.liveGps,
      T.popUpTicket,
      T.cateringInquiry,
    ],
    primaryCta: FAMILY_PRIMARY_CTA.route,
    copyTone: 'cafe',
  },
  /**
   * Bar / lounge — Getränkekarte + events / VIP; not Speisekarte-first.
   * Events story lives on About; drinks + cocktail stay on Home.
   */
  [FAMILY_ID.bar]: {
    id: FAMILY_ID.bar,
    verticals: [PLATFORM_VERTICAL_KEYS.bar],
    pages: [...LEGAL_AND_HOME, PAGE_SLUG.aboutUs, PAGE_SLUG.contact],
    excludeBlocks: [
      T.chefStory,
      T.kitchenTeam,
      T.tastingTimeline,
      T.liveGps,
      T.popUpTicket,
      T.preOrderPickup,
      T.coffeeSubscription,
      T.cateringInquiry,
      T.reservation,
    ],
    primaryCta: FAMILY_PRIMARY_CTA.order,
    copyTone: 'bar',
  },
  /**
   * Food truck — Standort/Route + Catering; never table reservation.
   * Catering story on About; stop / route stay on Home.
   */
  [FAMILY_ID.foodTruck]: {
    id: FAMILY_ID.foodTruck,
    verticals: [PLATFORM_VERTICAL_KEYS.foodTruck],
    pages: [...LEGAL_AND_HOME, PAGE_SLUG.aboutUs, PAGE_SLUG.contact],
    excludeBlocks: [
      T.reservation,
      T.chefStory,
      T.kitchenTeam,
      T.tastingTimeline,
      T.allergyForm,
      T.vipTableBooking,
      T.cocktailSpotlight,
    ],
    primaryCta: FAMILY_PRIMARY_CTA.route,
    copyTone: 'route',
  },
  /**
   * DE Imbiss — market: Speisekarte · hours · location · call/order.
   * No About / chef / table reservation (Mustafa’s, Curry 36, Konnopke, Rüyam patterns).
   */
  [FAMILY_ID.imbiss]: {
    id: FAMILY_ID.imbiss,
    verticals: [PLATFORM_VERTICAL_KEYS.restaurant],
    pages: [...LEGAL_AND_HOME, PAGE_SLUG.menu, PAGE_SLUG.contact],
    excludeBlocks: [
      T.chefStory,
      T.kitchenTeam,
      T.reservation,
      T.tastingTimeline,
      T.allergyForm,
      T.drinksList,
      T.privatePartyInquiry,
      /** Street Board Speisekarte — not live Dynamic Menu Flow. */
      T.dynamicMenu,
    ],
    primaryCta: FAMILY_PRIMARY_CTA.call,
    copyTone: 'street',
    menuHighlight: 'dishCards',
    menuFull: 'streetBoard',
  },
};

export function isFamilyId(value: string | undefined | null): value is FamilyId {
  return Boolean(value && (FAMILY_ID_VALUES as readonly string[]).includes(value));
}

/** Map legacy sushi/vietnam family ids onto Asian neighborhood. */
export function normalizeFamilyId(value: string | undefined | null): string | undefined {
  if (!value) return undefined;
  if (value in LEGACY_FAMILY_ID_ALIASES) {
    return LEGACY_FAMILY_ID_ALIASES[value as keyof typeof LEGACY_FAMILY_ID_ALIASES];
  }
  return value;
}

/**
 * Resolve family from a theme's `familyId` string (or undefined).
 * If the family's verticals don't include `vertical`, fall back to the vertical default
 * (e.g. Bold Street on food_truck → food_truck recipe, not Imbiss).
 */
export function resolveFamilyId(
  familyId: string | undefined,
  vertical: PlatformVerticalKey,
): FamilyId {
  const normalized = normalizeFamilyId(familyId);
  if (isFamilyId(normalized)) {
    const recipe = FAMILY_RECIPE[normalized];
    if (recipe.verticals.includes(vertical)) {
      return normalized;
    }
  }
  return DEFAULT_FAMILY_BY_VERTICAL[vertical];
}

export function resolveFamilyRecipe(
  familyId: string | undefined,
  vertical: PlatformVerticalKey,
): FamilyRecipe {
  return FAMILY_RECIPE[resolveFamilyId(familyId, vertical)];
}

/**
 * Onboarding / create locale policy.
 * Vietnam neighborhood themes → `de` + `vi`; else family `seedLocalePolicy`;
 * else platform `de` + `en`.
 */
export function resolveFamilySeedLocalePolicy(
  familyId: string | undefined,
  vertical: PlatformVerticalKey,
  themeId?: string,
): ProjectLocalePolicy {
  if (isVietnamNeighborhoodTheme(themeId)) {
    return ProjectLocalePolicySchema.parse({
      defaultLocale: DEFAULT_SITE_LOCALE,
      supportedLocales: [DEFAULT_SITE_LOCALE, 'vi'],
    });
  }
  const recipe = resolveFamilyRecipe(familyId, vertical);
  if (recipe.seedLocalePolicy) {
    return ProjectLocalePolicySchema.parse({
      defaultLocale: recipe.seedLocalePolicy.defaultLocale,
      supportedLocales: [...recipe.seedLocalePolicy.supportedLocales],
    });
  }
  return ProjectLocalePolicySchema.parse({
    defaultLocale: DEFAULT_SITE_LOCALE,
    supportedLocales: [...PLATFORM_LOCALE_ONBOARDING_DEFAULTS],
  });
}

/** True when create/onboarding should prefer seed locales over picker payload. */
export function usesSeedLocalePolicy(
  familyId: string | undefined,
  vertical: PlatformVerticalKey,
  themeId?: string,
): boolean {
  if (isVietnamNeighborhoodTheme(themeId)) return true;
  return Boolean(resolveFamilyRecipe(familyId, vertical).seedLocalePolicy);
}

/** True when the recipe allowlists pages and omits `/about-us`. */
export function familyRecipeOmitsAbout(recipe: FamilyRecipe): boolean {
  return Boolean(recipe.pages && !recipe.pages.includes(PAGE_SLUG.aboutUs));
}

export function familyExcludesBlock(
  recipe: FamilyRecipe,
  type: RegistryBlockType,
): boolean {
  return recipe.excludeBlocks.includes(type);
}

/** Expected theme → family wiring (registry `familyId` is source of truth). */
export const THEME_FAMILY_EXPECTATIONS: Record<ThemeId, FamilyId> = {
  [THEME_ID.michelinDark]: FAMILY_ID.fineDining,
  [THEME_ID.limestoneOrangery]: FAMILY_ID.fineDining,
  [THEME_ID.moorlandHearth]: FAMILY_ID.fineDining,
  [THEME_ID.bistroBrasserie]: FAMILY_ID.bistro,
  [THEME_ID.hinokiCounter]: FAMILY_ID.asian,
  [THEME_ID.lotusSteam]: FAMILY_ID.asian,
  [THEME_ID.tileAwning]: FAMILY_ID.asian,
  [THEME_ID.lanternCourt]: FAMILY_ID.asian,
  [THEME_ID.brutalistGrind]: FAMILY_ID.cafe,
  [THEME_ID.earthClay]: FAMILY_ID.cafe,
  [THEME_ID.neonNoir]: FAMILY_ID.bar,
  [THEME_ID.velvetVintage]: FAMILY_ID.bar,
  [THEME_ID.boldStreet]: FAMILY_ID.imbiss,
  [THEME_ID.mustardRail]: FAMILY_ID.imbiss,
  [THEME_ID.tahiniWindow]: FAMILY_ID.imbiss,
  [THEME_ID.cleanNomad]: FAMILY_ID.foodTruck,
};
